import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  PencilSimple,
  Trash,
  Package,
  Stack,
  Drop,
  Phone,
  EnvelopeSimple,
  MapPin,
  Storefront,
  UserCircle,
  ChatText,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { FileVault } from "@/components/FileVault";
import { PO_BADGE } from "@/pages/PurchaseOrders";
import { api, formatApiErrorDetail } from "@/lib/api";
import { loadCompany } from "@/lib/company";
import { siteImg } from "@/lib/siteContent";
import { repText, smsHref } from "@/lib/sms";

const SHOWROOM_MODE = {
  supplier: "This supplier's showroom",
  ours: "Our showroom instead",
  hidden: "Hidden — no box on the website",
};

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [companyName, setCompanyName] = useState("");

  const load = useCallback(async () => {
    const res = await api.get(`/suppliers/${id}/orders`);
    setData(res.data);
  }, [id]);

  useEffect(() => {
    load();
    loadCompany().then((c) => setCompanyName(c?.name || ""));
  }, [load]);

  if (!data) {
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Inventory / Supplier" />
        <main className="relative z-10 pt-6">
          <div className="h-40 animate-pulse rounded-lg bg-zinc-900" />
        </main>
      </div>
    );
  }

  const s = data.supplier;
  const openPOs = data.purchase_orders.filter(
    (p) => !["Received", "Cancelled"].includes(p.status),
  );
  const lastOrder = data.purchase_orders
    .map((p) => p.order_date || (p.created_at || "").slice(0, 10))
    .filter(Boolean)
    .sort()
    .pop();
  const daysOpen = (p) => {
    const from = p.order_date || (p.created_at || "").slice(0, 10);
    return from ? Math.max(0, Math.round((Date.now() - new Date(from)) / 86400000)) : null;
  };

  const remove = async () => {
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success("Supplier removed");
      navigate("/inventory/suppliers");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not remove supplier",
      );
    }
  };

  const contact = [
    [Phone, s.phone, `tel:${s.phone}`],
    [EnvelopeSimple, s.email, `mailto:${s.email}`],
    [MapPin, s.address, null],
  ].filter(([, v]) => v);

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Inventory / Supplier" />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/inventory/suppliers"
          data-testid="back-to-suppliers"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> All suppliers
        </Link>

        <div
          className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="supplier-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Supplier
              </div>
              <h1
                className="text-2xl font-bold tracking-tight sm:text-3xl"
                data-testid="supplier-name"
              >
                {s.name}
              </h1>
              {s.contact && (
                <p className="mt-1 text-sm text-zinc-400">{s.contact}</p>
              )}
              {(s.brands || []).length > 0 && (
                <div
                  className="mt-3 flex flex-wrap gap-2"
                  data-testid="supplier-brands"
                >
                  {s.brands.map((b) => (
                    <span
                      key={b.name}
                      data-testid={`supplier-brand-${b.name
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")}`}
                      title={
                        b.show_on_website === false
                          ? "Not shown on the website"
                          : "Shown in the website brands band"
                      }
                      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ${
                        b.show_on_website === false
                          ? "border-zinc-800 text-zinc-500"
                          : "border-indigo-400/40 bg-indigo-500/10 text-indigo-200"
                      }`}
                    >
                      {b.logo && (
                        <img
                          src={siteImg(b.logo)}
                          alt=""
                          className="h-4 w-auto max-w-[3rem] rounded-sm bg-white object-contain p-0.5"
                        />
                      )}
                      {b.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="new-po-for-supplier"
                onClick={() => navigate("/inventory/purchase-orders/new")}
                className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                New Purchase Order
              </Button>
              <Button
                data-testid="edit-supplier-button"
                variant="outline"
                onClick={() => navigate(`/inventory/suppliers/${id}/edit`)}
                className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
              >
                <PencilSimple size={15} weight="bold" className="mr-2" /> Edit
              </Button>
              <Button
                data-testid="delete-supplier-button"
                variant="outline"
                onClick={remove}
                className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest text-red-400 hover:bg-zinc-800"
              >
                <Trash size={15} weight="bold" />
              </Button>
            </div>
          </div>
          {contact.length > 0 && (
            <div
              className="mt-5 flex flex-wrap gap-4 border-t border-zinc-800 pt-4"
              data-testid="supplier-contact"
            >
              {contact.map(([Icon, value, href], i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-2 text-sm text-zinc-300"
                >
                  <Icon size={15} weight="bold" className="text-zinc-500" />
                  {href ? (
                    <a href={href} className="hover:text-yellow-400">
                      {value}
                    </a>
                  ) : (
                    value
                  )}
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {[
            ["POs", data.purchase_orders.length],
            ["Slabs ordered", data.slabs_ordered],
            ["Received", data.slabs_received],
            ["Slabs in stock", data.slabs_in_stock],
            ["Sinks on hand", data.sinks_on_hand],
          ].map(([k, v]) => (
            <div
              key={k}
              data-testid={`sup-stat-${k.toLowerCase().replace(/ /g, "-")}`}
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                {k}
              </div>
              <div className="font-mono-data mt-1 text-xl font-bold">{v}</div>
            </div>
          ))}
        </div>

        {(s.rep_name || s.rep_phone || s.rep_email) && (
          <div
            className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            data-testid="supplier-rep-card"
          >
            <div className="flex items-center gap-2">
              <UserCircle size={16} weight="bold" className="text-indigo-300" />
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Sales rep
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-base font-semibold" data-testid="supplier-rep-name">
                  {s.rep_name || "—"}
                </div>
                {s.rep_title && (
                  <div className="text-xs text-zinc-400" data-testid="supplier-rep-title">
                    {s.rep_title}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {s.rep_phone && (
                  <a
                    href={smsHref(s.rep_phone, repText(s, companyName))}
                    data-testid="supplier-rep-text"
                    className="inline-flex h-9 items-center gap-2 rounded-md bg-indigo-500 px-3 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                  >
                    <ChatText size={14} weight="bold" /> Text rep
                  </a>
                )}
                {s.rep_phone && (
                  <a
                    href={`tel:${s.rep_phone.replace(/[^\d+]/g, "")}`}
                    data-testid="supplier-rep-phone"
                    className="inline-flex h-9 items-center gap-2 rounded-md border border-zinc-700 px-3 text-xs uppercase tracking-widest hover:bg-zinc-800"
                  >
                    <Phone size={14} weight="bold" /> {s.rep_phone}
                  </a>
                )}
                {s.rep_email && (
                  <a
                    href={`mailto:${s.rep_email}`}
                    data-testid="supplier-rep-email"
                    className="inline-flex h-9 items-center gap-2 rounded-md border border-zinc-700 px-3 text-xs tracking-wide hover:bg-zinc-800"
                  >
                    <EnvelopeSimple size={14} weight="bold" /> {s.rep_email}
                  </a>
                )}
              </div>
            </div>
            {s.rep_notes && (
              <p
                className="mt-3 whitespace-pre-wrap text-sm text-zinc-300"
                data-testid="supplier-rep-notes"
              >
                {s.rep_notes}
              </p>
            )}
            <div
              className="mt-4 border-t border-zinc-800 pt-3"
              data-testid="supplier-rep-orders"
            >
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-400">
                <span data-testid="supplier-rep-open-count">
                  <span className="font-mono-data text-base font-bold text-zinc-100">
                    {openPOs.length}
                  </span>{" "}
                  open PO{openPOs.length === 1 ? "" : "s"}
                </span>
                <span data-testid="supplier-rep-last-order">
                  Last order:{" "}
                  <span className="font-mono-data text-zinc-200">{lastOrder || "never"}</span>
                </span>
              </div>
              {openPOs.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2" data-testid="supplier-rep-open-list">
                  {openPOs.map((p) => {
                    const d = daysOpen(p);
                    return (
                      <Link
                        key={p.id}
                        to={`/inventory/purchase-orders/${p.id}`}
                        data-testid={`supplier-rep-open-${p.id}`}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] ${
                          PO_BADGE[p.status] || PO_BADGE.Draft
                        } hover:brightness-125`}
                        title={p.expected_date ? `Expected ${p.expected_date}` : p.status}
                      >
                        <span className="font-mono-data font-bold">{p.code}</span>
                        <span className="uppercase tracking-widest">{p.status}</span>
                        {d !== null && <span className="opacity-80">{d}d</span>}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {s.notes && (
          <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              Notes
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm text-zinc-300">
              {s.notes}
            </p>
          </div>
        )}

        <div
          className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
          data-testid="supplier-showroom-card"
        >
          <div className="flex items-center gap-2">
            <Storefront size={16} weight="bold" className="text-yellow-400" />
            <div className="text-xs uppercase tracking-widest text-zinc-500">
              See it in person — shown on special-order colors
            </div>
          </div>
          <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
            {[
              ["On the website", SHOWROOM_MODE[s.showroom_mode] || SHOWROOM_MODE.supplier],
              ["Showroom", s.showroom_address || s.address || "—"],
              ["Hours", s.showroom_hours || "—"],
              ["Website", s.website || "—"],
              ["Visiting notes", s.visit_notes || "—"],
            ].map(([k, v]) => (
              <div key={k} data-testid={`supplier-showroom-${k.toLowerCase().replace(/ /g, "-")}`}>
                <dt className="text-[10px] uppercase tracking-widest text-zinc-500">{k}</dt>
                <dd className="mt-0.5 whitespace-pre-wrap text-zinc-200">{v}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="mt-6">
          <FileVault
            entity="suppliers"
            entityId={id}
            title="Supplier documents — contracts, price lists, invoices"
          />
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
            <div className="flex items-center gap-2">
              <Package size={16} weight="bold" className="text-indigo-300" />
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Purchase orders ({data.purchase_orders.length})
              </div>
            </div>
            <div className="mt-3 space-y-2" data-testid="supplier-po-list">
              {data.purchase_orders.length === 0 && (
                <p className="text-sm text-zinc-500">No purchase orders yet.</p>
              )}
              {data.purchase_orders.map((p) => (
                <Link
                  key={p.id}
                  to={`/inventory/purchase-orders/${p.id}`}
                  data-testid={`supplier-po-${p.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3 hover:border-zinc-700"
                >
                  <span className="min-w-0">
                    <span className="font-mono-data block text-xs text-yellow-500">
                      {p.code}
                    </span>
                    <span className="block truncate text-sm font-semibold">
                      {p.lines
                        .map((l) =>
                          l.kind === "sink" ? l.model : l.slab_color,
                        )
                        .filter(Boolean)
                        .join(", ") || "No lines"}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    {p.expected_date && (
                      <span className="font-mono-data text-xs text-zinc-400">
                        {p.expected_date}
                      </span>
                    )}
                    <span
                      className={`rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-widest ${
                        PO_BADGE[p.status] || PO_BADGE.Draft
                      }`}
                    >
                      {p.status}
                    </span>
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center gap-2">
                <Stack size={16} weight="bold" className="text-zinc-400" />
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Slabs supplied ({data.slabs.length})
                </div>
              </div>
              <div className="mt-3 grid gap-2" data-testid="supplier-slab-list">
                {data.slabs.length === 0 && (
                  <p className="text-sm text-zinc-500">
                    No slabs on file from this supplier.
                  </p>
                )}
                {data.slabs.map((x) => (
                  <Link
                    key={x.id}
                    to={`/inventory/slabs/${x.id}`}
                    data-testid={`supplier-slab-${x.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3 hover:border-zinc-700"
                  >
                    <span className="min-w-0">
                      <span className="font-mono-data block text-xs text-yellow-500">
                        {x.code}
                      </span>
                      <span className="block truncate text-sm font-semibold">
                        {x.slab_color}
                      </span>
                    </span>
                    <span className="font-mono-data shrink-0 text-xs text-zinc-400">
                      {x.status}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className="flex items-center gap-2">
                <Drop size={16} weight="bold" className="text-zinc-400" />
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Sinks supplied ({(data.sinks || []).length})
                </div>
              </div>
              <div className="mt-3 grid gap-2" data-testid="supplier-sink-list">
                {(data.sinks || []).length === 0 && (
                  <p className="text-sm text-zinc-500">
                    No sinks on file from this supplier.
                  </p>
                )}
                {(data.sinks || []).map((x) => (
                  <Link
                    key={x.id}
                    to={`/inventory/sinks/${x.id}`}
                    data-testid={`supplier-sink-${x.id}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3 hover:border-zinc-700"
                  >
                    <span className="min-w-0">
                      <span className="font-mono-data block text-xs text-yellow-500">
                        {x.code}
                      </span>
                      <span className="block truncate text-sm font-semibold">
                        {[x.brand, x.model].filter(Boolean).join(" ")}
                      </span>
                    </span>
                    <span className="font-mono-data shrink-0 text-xs text-zinc-400">
                      {x.stocked === false
                        ? "Supplier stock"
                        : `${x.quantity} on hand`}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
